<template>
    <card-tab-panel
        name="tasks"
        without-padding
        v-bind="$attrs"
        with-variable-height
    >
        <flex-container align-items="center" :padding="Size.md">
            <flex-container :size="8">
                <input-group v-model="responsibleFilterToggleValue">
                    <template #input>
                        <responsible-toggle-input
                            label="taken"
                            :count-active="myTasksCount"
                            :count-inactive="myTeamTasksCount"
                        />
                    </template>
                </input-group>
            </flex-container>

            <flex-container :size="2" justify-content="end">
                <input-group
                    :model-value="toggleCompletedTasks"
                    @input="toggleCompletedTasks = $event.target.value"
                >
                    <template #input>
                        <select-input
                            label-key-name="label"
                            value-key-name="value"
                            :items="[
                                {
                                    value: false,
                                    label: `Actief (${activeTasksCount})`,
                                }, {
                                    value: true,
                                    label: `Voltooid (${completedTasksCount})`,
                                },
                            ]"
                        />
                    </template>
                </input-group>
            </flex-container>
        </flex-container>

        <task-list
            :responsible-filter="responsibleFilter.value"
            :tasks="filteredTasks"
            :show-all="showAll"
        >
            <template #empty-message>
                <text-decorator :size="FontSize.sm">
                    <template v-if="props.lead">
                        <template v-if="showCompletedTasks">
                            Er zijn geen voltooide taken voor deze lead in "{{ responsibleFilter.label }}".
                        </template>
                        <template v-else>
                            Er zijn geen actieve taken voor deze lead in "{{ responsibleFilter.label }}".
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="showCompletedTasks">
                            Er zijn geen voltooide taken gevonden in "{{ responsibleFilter.label }}".
                        </template>
                        <template v-else>
                            Er zijn geen actieve taken gevonden in "{{ responsibleFilter.label }}".
                        </template>
                    </template>
                </text-decorator>
            </template>

            <template #footer-center>
                <button-text
                    v-if="props.lead"
                    :disabled="!props.editable"
                    @click="router.push(createRouteLocation({
                        name: 'ActionCreate',
                        params: {
                            leadId: props.lead.id.value,
                        },
                        query: {
                            type: 'task',
                        },
                    }, 'lead'))"
                >
                    <svg-icon :icon="IconAdd" />
                    <span>Taak toevoegen</span>
                </button-text>
                <contact-create-action-collapse v-else type="task">
                    <span>Taak toevoegen</span>
                </contact-create-action-collapse>
            </template>

            <template #footer-right="{ bindings }">
                <list-footer-action-show-more
                    v-if="bindings.count > 4"
                    @click="showAll = !showAll"
                >
                    <template v-if="showAll">
                        Bekijk minder
                    </template>
                    <template v-else>
                        Bekijk alle {{ bindings.count }}
                    </template>
                </list-footer-action-show-more>
            </template>
        </task-list>
    </card-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonText,
        CardTabPanel,
        FlexContainer,
        FontSize,
        InputGroup,
        ListFooterActionShowMore,
        Size,
        SvgIcon,
        TextDecorator,
    } from '@keyvue/one-ui';

    import TaskList from '@components/List/TaskList.vue';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import { type Lead, type Task, TaskStatus } from '@keyvue/one-api-client';
    import { DashboardView } from '@keyvue/one-api-client-legacy';
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import useParentRoute from '@composables/useParentRoute';
    import ContactCreateActionCollapse from '@components/Collapses/ContactCreateActionCollapse.vue';
    import ResponsibleToggleInput from '@components/Inputs/ResponsibleToggleInput.vue';

    export interface Props {
        editable?: boolean
        lead?: Lead
        taskCollection: ReactiveCollection<Task>;
    }

    const props = defineProps<Props>();
    const { currentUser, currentLegacyUser } = useClient();
    const router = useRouter();
    const createRouteLocation = useParentRoute();

    const showAll = ref(false);
    const responsibleFilterDefault: { value: 'user' | 'team', label: string } = currentLegacyUser.value?.defaultDashboardView === DashboardView.BRANCHES
        ? { value: 'team', label: 'Mijn team taken' }
        : { value: 'user', label: 'Mijn taken' };
    const responsibleFilter = ref<{ value: 'user' | 'team', label: string }>(responsibleFilterDefault);
    const responsibleValue = computed(() => responsibleFilter.value?.value ?? 'user');

    const responsibleFilterToggleValue = computed({
        get() {
            return responsibleFilter.value.value === 'user';
        },
        set(checked) {
            responsibleFilter.value = checked
                ? { value: 'user', label: 'Mijn taken' }
                : { value: 'team', label: 'Mijn team taken' };
        },
    });

    // Status filters
    const activeFilter = (task: Task) => [TaskStatus.CANCELED.value, TaskStatus.OPEN.value].includes(task.status.value);
    const completedFilter = (task: Task) => task.status.value === TaskStatus.COMPLETED.value;
    const currentStatusFilter = computed(() => (showCompletedTasks.value ? completedFilter : activeFilter));

    // Responsible filters
    const myTasksFilter = (task: Task) => task.assignedUsers.map((user) => user.id.toString()).includes(currentUser.value?.id.toString() ?? '');
    const myTeamTasksFilter = (task: Task) => !task.assignedUsers.map((user) => user.id.toString()).includes(currentUser.value?.id.toString() ?? '');
    const currentResponsibleFilter = computed(() => (responsibleValue.value === 'user' ? myTasksFilter : myTeamTasksFilter));

    // Counts based on current status filter
    const myTasksCount = computed(() => props.taskCollection.data.value.filter(currentStatusFilter.value).filter(myTasksFilter).length);
    const myTeamTasksCount = computed(() => props.taskCollection.data.value.filter(currentStatusFilter.value).filter(myTeamTasksFilter).length);
    const activeTasksCount = computed(() => props.taskCollection.data.value.filter(activeFilter).length);
    const completedTasksCount = computed(() => props.taskCollection.data.value.filter(completedFilter).length);

    // Filtered tasks
    const filteredTasks = computed(() => props.taskCollection.data.value
        .filter(currentStatusFilter.value)
        .filter(currentResponsibleFilter.value)
        .sort((a, b) => ((new Date(b.dueDateTime)).valueOf() - (new Date(a.dueDateTime)).valueOf())));

    // Initial value and workaround for fact that select-input cannot handle booleans
    // We push the value to the select-input as an object with a value and label instead of a boolean
    const toggleCompletedTasks = ref({ value: false, label: `Actief (${activeTasksCount.value})` });
    const showCompletedTasks = computed(() => toggleCompletedTasks.value?.value ?? false);
</script>
